/**
 * The checkout (`/checkout`): the steps on the left, the booking summary held
 * still on the right. A guest's pick waits in the address while they sign in;
 * once signed in the time is held, and from then on the summary shows the
 * server's own flow: the offering, date, time, length, place and total the
 * client confirms (design D7, D8).
 */
import type { SitePhoto } from '@telify/shared';

import { definePiece } from '../../contract/define';

export const contract = definePiece({
  name: 'checkout',
  description:
    'The checkout: the steps (sign in, forms, payment or the request) beside the booking summary, holding the chosen time once signed in.',
  drawing: 'standard-only',
  options: {},
  parts: {
    page: { description: 'The whole checkout.', required: false },
    back: { description: 'Back to the offering.', required: false },
    title: { description: 'The heading over the steps.', required: true, role: 'title' },
    steps: { description: 'The steps, in order.', required: true },
    step: { description: 'One step: its number or check, its title, and its body while it is the current one.', required: true, states: ['done', 'active', 'waiting'] },
    signedIn: { description: "Under a finished Sign in: the client's initials and name.", required: false },
    taken: { description: 'Shown when the chosen time was taken: the line and the next openings.', required: true },
    opening: { description: 'One of the next openings; holds that time instead.', required: true, role: 'time' },
    problem: { description: 'Why the time could not be held, in the server\'s words.', required: true },
    summary: { description: 'The booking summary beside the steps.', required: true },
    photo: { description: "The offering's photo in the summary.", required: false, role: 'photo' },
    offering: { description: "The offering's name, its length and format, and its price.", required: true },
    rows: { description: 'Date, time, duration and location.', required: true },
    phoneCall: {
      description:
        'Under the rows, for a service by phone: who calls and the number to call, with Change; or the number field with Save (and Cancel when a number is on file). While it asks, the step\'s Confirm or Pay waits.',
      required: true,
      states: ['asking'],
    },
    total: { description: 'The total and how it is paid.', required: true },
    holdNote: { description: 'Before sign-in: the time (or spot) is held once signed in.', required: false },
    otherBooking: { description: 'Another booking is still open: finish it, or cancel it and book this.', required: true },
  },
  actions: {
    back: { description: 'Goes back to the offering.', args: [] },
    pickOpening: { description: 'Holds one of the next openings instead of the taken time.', args: [{ name: 'startsAt', kind: 'item', list: 'openings[].times', key: 'startsAt' }] },
    continueOther: { description: 'Opens the other open booking in the checkout.', args: [] },
    startOver: { description: 'Cancels the other open booking, then holds this one.', args: [] },
    changePhone: { description: 'Opens the number to call for changing (Change).', args: [] },
    savePhone: {
      description: "Saves the number as the client's own, then reads the booking again so the line names it.",
      args: [{ name: 'phone', kind: 'value', type: 'string' }],
    },
    cancelPhone: { description: 'Closes the number field, keeping the number on file.', args: [] },
  },
  events: {
    held: 'The chosen time (or spot) is now held for the signed-in client.',
    timeTaken: 'The chosen time was taken before it could be held.',
  },
});

/** One step of the checkout. */
export interface CheckoutStep {
  /** Which step: sign in, the studio's forms (registration first), payment, or the request. */
  readonly id: 'signIn' | 'forms' | 'payment' | 'request';
  /** Its number, from 1. */
  readonly number: number;
  /** Its title: Sign in, Forms, Payment or Your request. */
  readonly title: string;
  /** done (a check), active (the one being filled in) or waiting (locked until the steps before it). */
  readonly status: 'done' | 'active' | 'waiting';
}

/** One day of the next openings. */
export interface CheckoutOpeningDay {
  /** The visitor's calendar day, YYYY-MM-DD. */
  readonly date: string;
  /** The day as drawn: "Wed, Nov 4". */
  readonly label: string;
  /** The open times that day. */
  readonly times: readonly { readonly startsAt: string; readonly label: string }[];
}

/** The booking summary: the server's flow once held, the visitor's pick before. */
export interface CheckoutSummary {
  /** The offering's name. */
  readonly name: string;
  /** Its photo, or null. */
  readonly photo: SitePhoto | null;
  /** The line under the name: "60 min · In person", "Group event · Online". */
  readonly sub: string;
  /** The price beside the name: "$120" or "Free". */
  readonly price: string;
  /** The day in words: "Tuesday, November 3"; null until known. */
  readonly date: string | null;
  /** The start with the zone: "10:00 AM EST"; null until known. */
  readonly time: string | null;
  /** The length: "60 min"; null for an event. */
  readonly duration: string | null;
  /** Where: the studio's place and city, or online and its platform; null when the studio set none. */
  readonly location: string | null;
  /** True for an online offering (the location row draws a video icon). */
  readonly online: boolean;
  /** True for an offering by phone (the location row draws a phone icon). */
  readonly byPhone: boolean;
  /** The start, ISO 8601; null until known. */
  readonly startsAt: string | null;
  /** Who it is with: the held flow's practitioner, or the studio's guide before the hold; null when unknown. */
  readonly provider: { readonly name: string; readonly photoUrl: string | null } | null;
  /** The total: "$120" or "Free". */
  readonly total: string;
  /** The total in dollars; null for a free one. */
  readonly amount: number | null;
  /** How it is paid: Paid now by card, Paid at the session, Collected after approval; null for a free one. */
  readonly totalNote: string | null;
  /** True once the summary is the server's own held flow. */
  readonly fromServer: boolean;
}

/** The number to call on a held booking by phone (service-by-phone D5). */
export interface CheckoutPhoneCall {
  /** "Dana Wilcher will call you at (512) 555-0199."; null while there is no number. */
  readonly line: string | null;
  /** The number on file; null when there is none. */
  readonly phone: string | null;
  /** No number on file: the field is open and says why. */
  readonly needsNumber: boolean;
  /** Change is open. */
  readonly changing: boolean;
  /** The field is open (no number, or Change): the step's Confirm or Pay waits. */
  readonly asking: boolean;
}

/** The other open booking, when the server holds one for this client. */
export interface CheckoutOtherBooking {
  /** Its offering's name, or null while unknown. */
  readonly name: string | null;
  /** Its start, ISO 8601, or null when it has no time yet. */
  readonly whenIso: string | null;
  /** Its offering's image, or null. */
  readonly imageUrl: string | null;
  /** website-photo-zoom: the image's crop as the studio set it (point and zoom), or null for centered. */
  readonly focalPointX: number | null;
  readonly focalPointY: number | null;
  readonly focalPointScale: number | null;
  /** True while it is being cancelled. */
  readonly cancelling: boolean;
}

/** What the checkout knows. */
export interface CheckoutState {
  /** loading until the pick (or the flow) is known; not-found for an address that names nothing bookable. */
  readonly status: 'loading' | 'ready' | 'not-found';
  /** Who is here: loading until known, then signed out or signed in. */
  readonly session: 'loading' | 'signedOut' | 'signedIn';
  /** The signed-in client's name and initials; null when signed out. */
  readonly client: { readonly name: string; readonly initials: string } | null;
  /** A service (a chosen time) or an event (its one time). */
  readonly kind: 'service' | 'event';
  /** True when the studio approves this booking first: a request. */
  readonly request: boolean;
  /** The heading: Sign in to confirm, Sign in to send your request, Complete payment, Review Your Booking or Review Your Request. */
  readonly title: string;
  /** Where Back goes, and its words; null when unknown. */
  readonly back: { readonly label: string; readonly href: string } | null;
  /** The steps, in order. */
  readonly steps: readonly CheckoutStep[];
  /** The hold: guest (waiting for sign-in), holding, held, taken (pick another), failed, or other (another booking is open). */
  readonly hold: 'guest' | 'holding' | 'held' | 'taken' | 'failed' | 'other';
  /** Before sign-in: "The time is held once you sign in." (or the spot); null after. */
  readonly holdNote: string | null;
  /** Why the time could not be held; null otherwise. */
  readonly problem: string | null;
  /** The service's next openings while the chosen time is taken; empty otherwise. */
  readonly openings: readonly CheckoutOpeningDay[];
  /** The booking summary; null while unknown. */
  readonly summary: CheckoutSummary | null;
  /** The number to call, once a booking by phone is held; null for any other. */
  readonly phoneCall: CheckoutPhoneCall | null;
  /** The other open booking while `hold` is other; null otherwise. */
  readonly otherBooking: CheckoutOtherBooking | null;
}

/** What a drawing can ask the checkout to do. */
export interface CheckoutActions {
  /** Go back to the offering. */
  readonly back: () => void;
  /** Hold this start (one of `openings`) instead of the taken time. */
  readonly pickOpening: (startsAt: string) => void;
  /** Open the other open booking in the checkout. */
  readonly continueOther: () => void;
  /** Cancel the other open booking, then hold this one. */
  readonly startOver: () => void;
  /** Open the number to call for changing. */
  readonly changePhone: () => void;
  /** Save the number as the client's own; rejects when it was not saved. */
  readonly savePhone: (phone: string) => Promise<void>;
  /** Close the number field, keeping the number on file. */
  readonly cancelPhone: () => void;
}

/** What the checkout tells its host. */
export interface CheckoutEvents {
  /** The time (or spot) is held; the flow's token names it from now on. */
  readonly held: { readonly flowToken: string };
  /** The chosen time was taken before it could be held. */
  readonly timeTaken: { readonly startsAt: string };
}
